import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './StationDetailsPage.css';
import {
    MapPin,
    Train,
    ArrowLeft,
    Building,
    Map,
    Clock,
    AlertCircle,
} from 'lucide-react';
import {
    fetchStationByCode,
    fetchSchedulesByStation,
    type StationFull,
    type ScheduleEntry,
} from '../api/stationsSchedules';
import toast from 'react-hot-toast';

const StationDetailsPage: React.FC = () => {
    const { stationCode } = useParams<{ stationCode: string }>();
    const navigate = useNavigate();

    const [station, setStation] = useState<StationFull | null>(null);
    const [trains, setTrains] = useState<ScheduleEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingTrains, setLoadingTrains] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Pagination for trains
    const [trainPage, setTrainPage] = useState(1);
    const [hasMoreTrains, setHasMoreTrains] = useState(true);

    const loadTrains = async (code: string, page: number) => {
        setLoadingTrains(true);
        try {
            const response = await fetchSchedulesByStation(code, page, 15);
            const newTrains = response.data || [];

            if (page === 1) {
                setTrains(newTrains);
            } else {
                setTrains(prev => [...prev, ...newTrains]);
            }

            setHasMoreTrains(newTrains.length === 15);
            setTrainPage(page);
        } catch (err) {
            toast.error('Failed to load train schedules');
        } finally {
            setLoadingTrains(false);
        }
    };

    useEffect(() => {
        if (!stationCode) return;

        const loadStation = async () => {
            setLoading(true);
            setError(null);
            try {
                const stationData = await fetchStationByCode(stationCode);
                setStation(stationData);
                await loadTrains(stationCode, 1);
            } catch (err: any) {
                setError(err.message || 'Failed to load station details');
                toast.error('Failed to load station information');
            } finally {
                setLoading(false);
            }
        };

        loadStation();
    }, [stationCode]);

    const loadMoreTrains = () => {
        if (stationCode) {
            loadTrains(stationCode, trainPage + 1);
        }
    };

    const handleTrainClick = (trainNumber: string) => {
        navigate(`/trains/${trainNumber}`);
    };

    if (loading) {
        return (
            <div className="station-details-loading">
                <div className="loading-spinner" />
                <p>Loading station details...</p>
            </div>
        );
    }

    if (error || !station) {
        return (
            <div className="station-details-error">
                <AlertCircle size={48} />
                <h2>Station Not Found</h2>
                <p>{error || `No station found with code "${stationCode}"`}</p>
                <button onClick={() => navigate('/stations')} className="back-btn">
                    <ArrowLeft size={18} /> Back to Stations
                </button>
            </div>
        );
    }

    const coordinates = station.geometry?.coordinates;

    return (
        <main className="station-details-page">
            {/* Header */}
            <header className="station-header">
                <button className="back-button" onClick={() => navigate('/stations')}>
                    <ArrowLeft size={20} />
                    All Stations
                </button>

                <div className="station-header-main">
                    <div className="station-code-badge">{station.code}</div>
                    <h1 className="station-name text-gradient-animate">{station.name}</h1>
                </div>
            </header>

            <div className="station-content">
                {/* Info Cards */}
                <div className="info-grid">
                    <div className="info-card">
                        <div className="info-card-rail" style={{ backgroundColor: 'var(--amber)' }}></div>
                        <div className="info-card-body">
                            <div className="info-icon"><Building size={24} /></div>
                            <div>
                                <p className="info-label">State</p>
                                <p className="info-value">{station.state}</p>
                            </div>
                        </div>
                    </div>

                    <div className="info-card">
                        <div className="info-card-rail" style={{ backgroundColor: 'var(--indigo)' }}></div>
                        <div className="info-card-body">
                            <div className="info-icon"><Map size={24} /></div>
                            <div>
                                <p className="info-label">Zone</p>
                                <p className="info-value">{station.zone}</p>
                            </div>
                        </div>
                    </div>

                    {coordinates && (
                        <div className="info-card">
                            <div className="info-card-rail" style={{ backgroundColor: 'var(--emerald)' }}></div>
                            <div className="info-card-body">
                                <div className="info-icon"><MapPin size={24} /></div>
                                <div>
                                    <p className="info-label">Coordinates</p>
                                    <p className="info-value">
                                        {coordinates[1].toFixed(4)}°N, {coordinates[0].toFixed(4)}°E
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Map Placeholder */}
                {coordinates && (
                    <div className="station-map">
                        <div className="map-header">
                            <MapPin size={18} />
                            <span>Location on Map</span>
                        </div>
                        <div className="map-placeholder">
                            <p>📍 Interactive Map Coming Soon</p>
                            <p className="map-coords">
                                Lat: {coordinates[1].toFixed(6)} | Lng: {coordinates[0].toFixed(6)}
                            </p>
                        </div>
                    </div>
                )}

                {/* Upcoming Trains */}
                <section className="trains-section">
                    <div className="section-header">
                        <div className="section-title">
                            <Train size={22} />
                            <h2>Upcoming / Scheduled Trains</h2>
                        </div>
                        <p className="section-subtitle">Click on any train to see full journey details</p>
                    </div>

                    {trains.length === 0 && !loadingTrains ? (
                        <div className="no-trains">
                            <Clock size={40} />
                            <p>No scheduled trains found for this station.</p>
                        </div>
                    ) : (
                        <div className="trains-list">
                            {trains.map((train, index) => (
                                <div
                                    key={`${train.train_number}-${index}`}
                                    className="train-card"
                                    onClick={() => handleTrainClick(train.train_number)}
                                >
                                    <div 
                                        className="tc-rail-inner"
                                        style={{ backgroundColor: ['var(--indigo)', 'var(--teal)', 'var(--amber)', 'var(--rose)', 'var(--emerald)', 'var(--violet)'][index % 6] }}
                                    ></div>
                                    <div className="tc-body-inner">
                                        <div className="train-left">
                                            <div className="train-number">{train.train_number}</div>
                                            <div className="train-name">{train.train_name}</div>
                                        </div>

                                        <div className="train-timing">
                                            <div className="timing">
                                                <span className="time-label">ARR</span>
                                                <span className="time">{train.arrival || '--:--'}</span>
                                            </div>
                                            <div className="timing">
                                                <span className="time-label">DEP</span>
                                                <span className="time">{train.departure || '--:--'}</span>
                                            </div>
                                        </div>

                                        <div className="train-day">
                                            Day {train.day}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {hasMoreTrains && (
                        <div className="load-more-container">
                            <button
                                className="load-more-btn"
                                onClick={loadMoreTrains}
                                disabled={loadingTrains}
                            >
                                {loadingTrains ? 'Loading...' : 'Load More Trains'}
                            </button>
                        </div>
                    )}
                </section>
            </div>
        </main>
    );
};

export default StationDetailsPage;